import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, User, Key, Shield, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('password'); // password | mfa | mfa_setup
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [userId, setUserId] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  const [provisioningUri, setProvisioningUri] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  // Check if already logged in
  useEffect(() => {
    const checkSession = async () => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/session`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.authenticated) {
            navigate('/admin/ops');
            return;
          }
        } catch (e) {
          localStorage.removeItem('auth_token');
        }
      }
      setCheckingSession(false);
    };
    checkSession();
  }, [navigate]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });

      const data = response.data;

      if (data.status === 'success') {
        // Login complete (no MFA)
        localStorage.setItem('auth_token', data.token);
        toast.success(`Welcome, ${data.username}`);
        navigate('/admin/ops');
      } else if (data.status === 'mfa_required') {
        // MFA required
        setUserId(data.user_id);
        setStep('mfa');
        toast.info('Enter your authenticator code');
      } else if (data.status === 'mfa_setup_required') {
        // Need to set up MFA first
        setUserId(data.user_id);
        await setupMFA(data.user_id);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.response?.data?.error || 'Login failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const setupMFA = async (uid) => {
    try {
      // We need a temporary token to set up MFA
      // For now, use the user_id directly
      const response = await axios.post(`${API}/auth/mfa/setup`, {}, {
        headers: { Authorization: `Bearer temp:${uid}` }
      });
      
      if (response.data.status === 'setup_pending') {
        setMfaSecret(response.data.secret);
        setProvisioningUri(response.data.provisioning_uri);
        setStep('mfa_setup');
        toast.info('Set up your authenticator app');
      }
    } catch (error) {
      toast.error('Failed to start MFA setup');
    }
  };

  const handleMFASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/mfa/verify`, {
        user_id: userId,
        code: mfaCode
      });

      if (response.data.status === 'success') {
        localStorage.setItem('auth_token', response.data.token);
        toast.success(`Welcome, ${response.data.username}`);
        navigate('/admin/ops');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Invalid code';
      toast.error(msg);
      setMfaCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleMFASetupVerify = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/mfa/verify-setup`, {
        user_id: userId,
        code: mfaCode
      });

      if (response.data.status === 'mfa_enabled') {
        toast.success('MFA enabled! Please log in again.');
        setStep('password');
        setMfaCode('');
        setPassword('');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Invalid code';
      toast.error(msg);
      setMfaCode('');
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-zinc-900 border-zinc-800">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-12 h-12 bg-zinc-800 rounded-xl flex items-center justify-center">
            <Shield className="h-6 w-6 text-emerald-400" />
          </div>
          <CardTitle className="text-xl font-mono text-white">
            Jigsaw SEO Autopilot
          </CardTitle>
          <CardDescription className="text-zinc-500">
            {step === 'password' && 'Sign in to continue'}
            {step === 'mfa' && 'Enter your authenticator code'}
            {step === 'mfa_setup' && 'Set up two-factor authentication'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                  <Input
                    type="text"
                    placeholder="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                    autoFocus
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                  <Input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          )}

          {step === 'mfa' && (
            <form onSubmit={handleMFASubmit} className="space-y-4">
              <div className="space-y-2">
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                  <Input
                    type="text"
                    placeholder="000000"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 text-center font-mono text-xl tracking-widest"
                    autoFocus
                    maxLength={6}
                    required
                  />
                </div>
                <p className="text-xs text-zinc-500 text-center">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>
              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={loading || mfaCode.length !== 6}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Verify'
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full text-zinc-500"
                onClick={() => { setStep('password'); setMfaCode(''); }}
              >
                Back to login
              </Button>
            </form>
          )}

          {step === 'mfa_setup' && (
            <div className="space-y-4">
              <div className="bg-zinc-800 p-4 rounded-lg text-center">
                <p className="text-sm text-zinc-400 mb-3">
                  Scan this code with your authenticator app:
                </p>
                {/* QR Code placeholder - in production use a QR library */}
                <div className="bg-white p-4 rounded-lg inline-block">
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(provisioningUri)}`}
                    alt="MFA QR Code"
                    className="w-48 h-48"
                  />
                </div>
                <p className="text-xs text-zinc-500 mt-3">
                  Or enter manually: <code className="bg-zinc-700 px-2 py-0.5 rounded text-xs">{mfaSecret}</code>
                </p>
              </div>
              <form onSubmit={handleMFASetupVerify} className="space-y-4">
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                  <Input
                    type="text"
                    placeholder="000000"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 text-center font-mono text-xl tracking-widest"
                    maxLength={6}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  disabled={loading || mfaCode.length !== 6}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Enable MFA'
                  )}
                </Button>
              </form>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
